import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// ONE VENUE, AS A PLAYER WHO IS NOT ITS MEMBER SEES IT (#833).
//
// `GET /venues/:slug` — the public face of one organization: its name, kind,
// the opening hours its calendar enforces, and its courts. Authenticated with
// the account session the player app already holds; it answers the same for
// every caller, because there is nothing in it that depends on who asks.
//
// NOT A BOOKING READ. There is no availability and no price here; both are
// later slices of #833, and the screen says so rather than implying them.
// ─────────────────────────────────────────────────────────────────────

export interface VenueCourt {
  number: number;
  /** The centre's own name for the court, or null for «Bane N». */
  name: string | null;
  type: 'double' | 'single';
}

export interface Venue {
  slug: string;
  name: string;
  /** `CENTRE` or `CLUB`, to label it. */
  kind: string;
  /** Null when the centre has never configured its booking calendar. */
  openingHours: { opens: string; closes: string } | null;
  courts: VenueCourt[];
}

/**
 * The outcome, with «does not exist» and «could not ask» kept apart — the
 * same line `searchClubsOutcome` draws, for the same reason: telling somebody
 * a venue does not exist because the server did not answer is a lie about the
 * venue. A STRING discriminant, because `strict: false` does not narrow a
 * boolean-literal union (see `clubs.ts`).
 */
export type VenueOutcome =
  | { status: 'ok'; venue: Venue }
  | { status: 'not_found' }
  | { status: 'aborted' }
  | { status: 'unavailable' };

const UNAVAILABLE = { status: 'unavailable' } as const;

export async function readVenue(slug: string, signal?: AbortSignal): Promise<VenueOutcome> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch(`/venues/${encodeURIComponent(slug)}`, { signal });
  } catch {
    if (signal?.aborted) return { status: 'aborted' };
    return UNAVAILABLE;
  }
  if (res.status === 404) return { status: 'not_found' };
  if (!res.ok) return UNAVAILABLE;
  const body = (await res.json().catch(() => null)) as { venue?: unknown } | null;
  if (signal?.aborted) return { status: 'aborted' };
  const venue = toVenue(body?.venue);
  return venue ? { status: 'ok', venue } : UNAVAILABLE;
}

/** SHAPED, NOT TRUSTED — a venue without a slug or name is not one to show. */
function toVenue(raw: unknown): Venue | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.slug !== 'string' || !r.slug) return null;
  if (typeof r.name !== 'string' || !r.name) return null;
  const hours = r.openingHours as { opens?: unknown; closes?: unknown } | null | undefined;
  const openingHours =
    hours && typeof hours.opens === 'string' && typeof hours.closes === 'string'
      ? { opens: hours.opens, closes: hours.closes }
      : null;
  const courts: VenueCourt[] = [];
  for (const c of Array.isArray(r.courts) ? r.courts : []) {
    const court = c as Record<string, unknown> | null;
    if (!court || !Number.isFinite(court.number)) continue;
    courts.push({
      number: Number(court.number),
      name: typeof court.name === 'string' && court.name ? court.name : null,
      type: court.type === 'single' ? 'single' : 'double',
    });
  }
  return {
    slug: r.slug,
    name: r.name,
    kind: typeof r.kind === 'string' ? r.kind : '',
    openingHours,
    courts,
  };
}

// ─────────────────────────────────────────────────────────────────────
// WHAT IS FREE THERE (#833 slice 2).
//
// `GET /venues/:slug/availability?date=` — one day's free start times at the
// venue, and the courts a booking there could go on, by the MEMBER booking's
// own rules: the server runs the same slot engine `POST /member/bookings`
// checks against, so a time shown here is one a member could take.
//
// Occupancy only. No booking, no person, no price: membership decides the
// price, and non-member pricing is not decided — so nothing here may be read
// as one.
// ─────────────────────────────────────────────────────────────────────

export interface VenueFreeSlot {
  startTime: string;
  endTime: string;
  /** Court NUMBERS — `Venue.courts[].number`. */
  courts: number[];
}

export interface VenueAvailability {
  /** The day answered, venue-local `YYYY-MM-DD`. */
  date: string;
  /** Every day the server answers for, from the venue's today. */
  dates: string[];
  /** The length every slot is for: the shortest the venue allows. */
  durationMin: number;
  slots: VenueFreeSlot[];
}

/** `availability: null` is the server saying the venue has no calendar to show. */
export type VenueAvailabilityOutcome =
  | { status: 'ok'; availability: VenueAvailability | null }
  | { status: 'not_found' }
  | { status: 'aborted' }
  | { status: 'unavailable' };

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const HHMM = /^\d{2}:\d{2}$/;

/** One day of the venue's free courts; `date` omitted asks for the venue's today. */
export async function readVenueAvailability(
  slug: string,
  date?: string,
  signal?: AbortSignal,
): Promise<VenueAvailabilityOutcome> {
  const query = date ? `?date=${encodeURIComponent(date)}` : '';
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch(`/venues/${encodeURIComponent(slug)}/availability${query}`, { signal });
  } catch {
    if (signal?.aborted) return { status: 'aborted' };
    return UNAVAILABLE;
  }
  if (res.status === 404) return { status: 'not_found' };
  if (!res.ok) return UNAVAILABLE;
  const body = (await res.json().catch(() => null)) as { availability?: unknown } | null;
  if (signal?.aborted) return { status: 'aborted' };
  if (!body || !('availability' in body)) return UNAVAILABLE;
  if (body.availability === null) return { status: 'ok', availability: null };
  const availability = toAvailability(body.availability);
  return availability ? { status: 'ok', availability } : UNAVAILABLE;
}

/** SHAPED, NOT TRUSTED — a day that is not a day is not one to show. */
function toAvailability(raw: unknown): VenueAvailability | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.date !== 'string' || !DAY.test(r.date)) return null;
  const dates = (Array.isArray(r.dates) ? r.dates : []).filter(
    (d): d is string => typeof d === 'string' && DAY.test(d),
  );
  const slots: VenueFreeSlot[] = [];
  for (const s of Array.isArray(r.slots) ? r.slots : []) {
    const slot = s as Record<string, unknown> | null;
    if (!slot || typeof slot.startTime !== 'string' || !HHMM.test(slot.startTime)) continue;
    if (typeof slot.endTime !== 'string' || !HHMM.test(slot.endTime)) continue;
    const courts = (Array.isArray(slot.courts) ? slot.courts : []).filter((c): c is number =>
      Number.isInteger(c),
    );
    if (courts.length > 0) slots.push({ startTime: slot.startTime, endTime: slot.endTime, courts });
  }
  return {
    date: r.date,
    dates,
    durationMin: Number.isFinite(r.durationMin) ? Number(r.durationMin) : 0,
    slots,
  };
}
